import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { APP_NAME } from '../../config/appConstants';
import AuthPageFooter from '../../shared/components/common/AuthPageFooter';
import '../../App.css';
import '../Login/Login.css';
import '../VerifyEmail/VerifyEmail.css';
import appLogo from '../../assets/images/DevicePulse-app-logo2.png';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('validating'); // validating, form, success, error
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract token from URL query parameters
    const params = new URLSearchParams(location.search);
    const resetToken = params.get('token');

    if (!resetToken) {
      setError('Invalid reset link. Please request a new password reset.');
      setStatus('error');
      return;
    }

    setToken(resetToken);

    // Validate token
    const validateToken = async () => {
      try {
        const response = await axios.post(API_ENDPOINTS.AUTH.VALIDATE_RESET_TOKEN, {
          token: resetToken
        });

        if (response.data.valid) {
          setStatus('form');
        } else {
          setError('This reset link is invalid or has expired. Please request a new password reset.');
          setStatus('error');
        }
      } catch (err) {
        setError('Error validating reset link. Please try again.');
        setStatus('error');
      }
    };

    validateToken();
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await axios.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        token,
        new_password: password
      });

      setStatus('success');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left Side - Animated Gradient Background */}
      <div className="login-left-side">
        {/* Background Layers */}
        <div className="particles"></div>
        <div className="medical-pattern"></div>

        {/* Floating Medical Device Illustrations */}
        <div className="medical-float-1">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="35" y="20" width="50" height="70" rx="5" fill="white" opacity="0.9"/>
            <rect x="30" y="85" width="60" height="25" rx="3" fill="white" opacity="0.9"/>
            <circle cx="60" cy="55" r="15" fill="rgba(102,126,234,0.3)"/>
            <rect x="55" y="30" width="10" height="15" fill="rgba(102,126,234,0.4)"/>
            <line x1="40" y1="95" x2="80" y2="95" stroke="rgba(102,126,234,0.5)" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-2">
          <svg width="140" height="100" viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="70" cy="50" rx="60" ry="35" fill="white" opacity="0.9"/>
            <ellipse cx="70" cy="50" rx="30" ry="20" fill="rgba(102,126,234,0.3)"/>
            <rect x="10" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <rect x="115" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <circle cx="70" cy="30" r="5" fill="rgba(102,126,234,0.5)"/>
          </svg>
        </div>

        <div className="medical-float-3">
          <svg width="100" height="120" viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="20" y="30" width="60" height="70" rx="8" fill="white" opacity="0.9"/>
            <rect x="30" y="45" width="40" height="25" rx="3" fill="rgba(102,126,234,0.3)"/>
            <circle cx="35" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
            <circle cx="65" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
            <rect x="40" y="20" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
            <line x1="30" y1="55" x2="70" y2="55" stroke="white" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-4">
          <svg width="110" height="110" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="55" cy="35" r="20" fill="white" opacity="0.9"/>
            <rect x="50" y="50" width="10" height="40" fill="white" opacity="0.9"/>
            <circle cx="55" cy="35" r="12" fill="rgba(102,126,234,0.3)"/>
            <path d="M 45,30 L 55,25 L 65,30" stroke="white" strokeWidth="2" fill="none"/>
            <rect x="35" y="85" width="40" height="15" rx="5" fill="white" opacity="0.8"/>
          </svg>
        </div>

        <div className="medical-float-5">
          <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="15" y="15" width="90" height="60" rx="5" fill="white" opacity="0.9"/>
            <rect x="20" y="20" width="80" height="50" rx="3" fill="rgba(102,126,234,0.3)"/>
            <path d="M 30,45 L 45,45 L 50,35 L 55,55 L 60,40 L 70,45 L 90,45" stroke="rgba(102,126,234,0.6)" strokeWidth="3" fill="none"/>
            <rect x="45" y="75" width="30" height="5" fill="white" opacity="0.8"/>
            <rect x="35" y="80" width="50" height="15" rx="3" fill="white" opacity="0.9"/>
          </svg>
        </div>

        <div className="medical-float-6">
          <svg width="100" height="110" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="25" y="25" width="50" height="60" rx="6" fill="white" opacity="0.9"/>
            <rect x="35" y="35" width="30" height="20" rx="3" fill="rgba(102,126,234,0.3)"/>
            <circle cx="40" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
            <circle cx="60" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
            <rect x="40" y="85" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
          </svg>
        </div>

        {/* Left Side Content */}
        <div className="left-side-content">
          <div className="left-side-text">
            <h2>Welcome to</h2>
            <h1>
              <img src={appLogo} className="emoji-app-logo" alt="App Logo" />
              {APP_NAME}
            </h1>
            <p>Secure your account by resetting your password</p>
          </div>
        </div>

      </div>

      {/* Right Side - Password Reset Form */}
      <div className="login-right-side">
        <AuthPageFooter />

        <div className="login-box">
          {status !== 'success' && (
            <div className="login-header">
              <h3>{status === 'error' ? 'Reset Failed' : 'Reset Your Password'}</h3>
              <p>{status === 'error' ? 'Unable to process your request' : 'Enter your new password below'}</p>
            </div>
          )}

          {status === 'validating' && (
            <div className="status-container verifying">
              <div className="spinner"></div>
              <p>Validating reset link...</p>
            </div>
          )}

          {status === 'form' && (
            <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
              <div className="form-group">
                <label htmlFor="password" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  New Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password (minimum 8 characters)"
                  required
                  minLength="8"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    marginBottom: '8px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                  Password must be at least 8 characters long
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '8px', marginTop: '16px', fontWeight: '500' }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength="8"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    marginBottom: '12px'
                  }}
                />
              </div>

              {error && (
                <div style={{
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  padding: '12px',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  fontSize: '0.9rem'
                }}>
                  {error}
                </div>
              )}

              <div className="auth-actions">
                <button
                  type="submit"
                  className="action-btn action-primary action-btn-block"
                  disabled={loading}
                >
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </button>

                <button
                  type="button"
                  className="action-btn action-secondary action-btn-block"
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}

          {status === 'success' && (
            <>
              <div style={{
                textAlign: 'center',
                paddingTop: '20px',
                paddingBottom: '10px',
                borderBottom: '1px solid #f0f0f0',
                marginBottom: '30px'
              }}>
                <h2 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '24px' }}>
                  Password Reset Successfully! ✓
                </h2>
                <p style={{ margin: '0', color: '#7f8c8d', fontSize: '14px' }}>
                  Your password has been updated and secured
                </p>
              </div>

              <div className="status-container success">
                <div className="success-icon">✓</div>

                <div style={{
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #81c784',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    margin: '0',
                    color: '#2e7d32',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    fontWeight: '500'
                  }}>
                    🔐 Your account is now secure with your new password.<br/>
                    You can log in with your new credentials.
                  </p>
                </div>

                <button
                  type="button"
                  className="action-btn action-primary action-btn-block"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <div className="status-container error">
              <div className="error-icon">✕</div>
              <h2>Invalid Reset Link</h2>
              <p>{error || 'This reset link is invalid or has expired.'}</p>
              <div className="auth-actions">
                <button
                  type="button"
                  className="action-btn action-primary action-btn-block"
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </button>
                <button
                  type="button"
                  className="action-btn action-secondary action-btn-block"
                  onClick={() => navigate('/forgot-password')}
                >
                  Request New Reset Link
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
